import { createAuthenticatedApi } from "@/lib/utils/api/api-client";
import { createOpenApiConfiguration } from "@/lib/utils/api/openapi-configuration";
import { MediaApi } from "@/openapi/generated";
import type { GetMediaResponse } from "@/openapi/generated/models";
import { FileType, MediaGroup } from "@/openapi/generated/models";

export function getMediaIdAsNumber(
  media: GetMediaResponse | null | undefined,
): number {
  if (!media?.mediaId) return 0;
  const id = Number(media.mediaId);
  return Number.isFinite(id) ? id : 0;
}

/** Parse media id from API upload/list payloads (number, string, or nested). */
export function parseMediaIdFromUnknown(value: unknown): number {
  if (value == null) return 0;
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : 0;
}

export function mediaListContainsId(
  items: GetMediaResponse[] | null | undefined,
  mediaId: number,
): boolean {
  if (!items?.length || mediaId <= 0) return false;
  return items.some(
    (item) => parseMediaIdFromUnknown(item.mediaId) === mediaId,
  );
}

/** Parse owner id embedded in storage paths like `…/Teacher/42/profile.png`. */
export function getOwnerIdFromMediaPath(
  value: string,
  ownerType: MediaOwnerType,
): number | null {
  const normalized = value.replace(/\\/g, "/");
  const pattern = new RegExp(`${ownerType}/(\\d+)(?:/|$)`, "i");
  const match = normalized.match(pattern);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Drop media rows that clearly belong to a different owner (tenant-global mediaId pool).
 * Rows without path hints are kept for backward compatibility.
 */
export function mediaItemBelongsToOwner(
  item: GetMediaResponse,
  ownerType: MediaOwnerType,
  ownerId: number,
): boolean {
  const hints = [
    item.mediaKey,
    item.fileName,
    item.url,
    item.externalUrl,
  ].filter(
    (value): value is string =>
      typeof value === "string" && value.trim().length > 0,
  );

  if (hints.length === 0) {
    return true;
  }

  for (const hint of hints) {
    const ownerFromPath = getOwnerIdFromMediaPath(hint, ownerType);
    if (ownerFromPath != null) {
      return ownerFromPath === ownerId;
    }
  }

  return true;
}

export function filterMediaItemsForOwner(
  items: GetMediaResponse[],
  ownerType: MediaOwnerType,
  ownerId: number,
): GetMediaResponse[] {
  return items.filter((item) =>
    mediaItemBelongsToOwner(item, ownerType, ownerId),
  );
}

/** When uploads leave duplicate media rows, always prefer the newest record. */
export function pickLatestMedia(
  items: GetMediaResponse[],
  predicate: (item: GetMediaResponse) => boolean,
): GetMediaResponse | null {
  const matches = items.filter(predicate);
  if (matches.length === 0) return null;

  return matches.reduce((latest, item) =>
    getMediaIdAsNumber(item) > getMediaIdAsNumber(latest) ? item : latest,
  );
}

export function pickMediaItemUrl(
  item: GetMediaResponse | null | undefined,
): string | null {
  return item?.externalUrl?.trim() || item?.url?.trim() || null;
}

export type MediaOwnerType =
  | "User"
  | "Class"
  | "Teacher"
  | "Student"
  | "Team"
  | "Event"
  | "Location"
  | "Tenant"
  | "ClassRoom"
  | "Season";

export interface MediaAssetResult {
  url: string | null;
  mediaId: number | null;
  media: GetMediaResponse | null;
}

export async function fetchMediaAsset(
  {
    tenantId,
    ownerType,
    ownerId,
    mediaGroup,
  }: {
    tenantId: number;
    ownerType: MediaOwnerType;
    ownerId: number;
    mediaGroup: MediaGroup;
  },
  options?: { accessToken?: string },
): Promise<MediaAssetResult> {
  const service = options?.accessToken
    ? createAuthenticatedApi(MediaApi, options.accessToken)
    : new MediaApi(createOpenApiConfiguration());

  const mediaList = await service.apiMediaTypeIdGet({
    type: ownerType as never,
    id: ownerId,
    xTenantID: Number(tenantId),
  });

  const items = filterMediaItemsForOwner(mediaList || [], ownerType, ownerId);

  let media = pickLatestMedia(items, (item) => item.mediaGroup === mediaGroup);

  if (!media && mediaGroup === MediaGroup.Profile) {
    if (ownerType === "Teacher") {
      media = pickLatestMedia(
        items,
        (item) => item.mediaGroup === MediaGroup.Cover,
      );
    } else if (!media) {
      media = pickLatestMedia(
        items,
        (item) => item.fileType === FileType.Image,
      );
    }
  }

  const url = pickMediaItemUrl(media);

  return {
    url,
    mediaId: media?.mediaId != null ? Number(media.mediaId) : null,
    media,
  };
}

/** Pick the first non-empty media URL from candidates (media API, entity field, etc.). */
export function resolvePreferredMediaUrl(
  ...candidates: Array<string | null | undefined>
): string | null {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

/** Cover image for a class: media API first, then class response field. */
export function resolveClassCoverUrl(
  mediaUrl: string | null | undefined,
  classImageUrl: string | null | undefined,
): string | null {
  return resolvePreferredMediaUrl(mediaUrl, classImageUrl);
}

/** Cover/hero image for a location (media API only - no legacy field on entity). */
export function resolveLocationCoverUrl(
  mediaUrl: string | null | undefined,
): string | null {
  return resolvePreferredMediaUrl(mediaUrl);
}

/** Cover for a season homepage panel: media API first, then optional response field. */
export function resolveSeasonCoverUrl(
  mediaUrl: string | null | undefined,
  seasonImageUrl?: string | null | undefined,
): string | null {
  return resolvePreferredMediaUrl(mediaUrl, seasonImageUrl);
}

export async function fetchLocationCoverImageUrl(
  tenantId: number,
  locationId: number,
): Promise<string | null> {
  try {
    const media = await fetchMediaAsset({
      tenantId,
      ownerType: "Location",
      ownerId: locationId,
      mediaGroup: MediaGroup.Cover,
    });
    return resolveLocationCoverUrl(media.url);
  } catch {
    return null;
  }
}

/** Profile/hero image for a teacher: Profile media, then Cover, then any image from list. */
export function resolveTeacherProfileUrl(
  profileMediaUrl: string | null | undefined,
  coverMediaUrl?: string | null | undefined,
): string | null {
  return resolvePreferredMediaUrl(profileMediaUrl, coverMediaUrl);
}

/** Cover image for a classroom (media API only). */
export function resolveClassRoomCoverUrl(
  mediaUrl: string | null | undefined,
): string | null {
  return resolvePreferredMediaUrl(mediaUrl);
}

export async function fetchClassRoomCoverImageUrl(
  tenantId: number,
  classRoomId: number,
): Promise<string | null> {
  try {
    const media = await fetchMediaAsset({
      tenantId,
      ownerType: "ClassRoom",
      ownerId: classRoomId,
      mediaGroup: MediaGroup.Cover,
    });
    return resolveClassRoomCoverUrl(media.url);
  } catch {
    return null;
  }
}

export async function fetchTeacherProfileImageUrl(
  tenantId: number,
  teacherId: number,
): Promise<string | null> {
  try {
    const result = await fetchMediaAsset({
      tenantId,
      ownerType: "Teacher",
      ownerId: teacherId,
      mediaGroup: MediaGroup.Profile,
    });
    return resolveTeacherProfileUrl(result.url);
  } catch {
    return null;
  }
}
